import React, { useCallback, useState } from 'react';
import restaurantService from '../../services/restaurantService';
import { notifyCaptainTableRequested } from '../../utils/captainPanelEvents';
import type { AiCaptainTableItem } from '../../utils/aiCaptainTables';
import { formatTableLabel } from '../../utils/aiCaptainTables';

interface CaptainTableCardsProps {
  tables: AiCaptainTableItem[];
  appName: string;
  onTableRequested?: (tableNumber: number, allotted: boolean) => void;
}

const CaptainTableCards: React.FC<CaptainTableCardsProps> = ({
  tables,
  appName,
  onTableRequested,
}) => {
  const [requestingId, setRequestingId] = useState<number | null>(null);
  const [confirmedId, setConfirmedId] = useState<number | null>(null);

  const handleSelectTable = useCallback(
    async (table: AiCaptainTableItem) => {
      if (requestingId != null) return;
      setRequestingId(table.id);
      try {
        const response = await restaurantService.createDineInTableRequest(appName, table.id);
        if (response.success && response.tableNumber != null) {
          setConfirmedId(table.id);
          notifyCaptainTableRequested(response.tableNumber, response.allotted === true);
          onTableRequested?.(response.tableNumber, response.allotted === true);
        }
      } finally {
        setRequestingId(null);
      }
    },
    [appName, onTableRequested, requestingId]
  );

  if (tables.length === 0) return null;

  return (
    <div className="vc-table-cards" role="list" aria-label="Available tables">
      {tables.map((table) => {
        const isRequesting = requestingId === table.id;
        const isConfirmed = confirmedId === table.id;

        return (
          <article key={table.id} className="vc-table-card" role="listitem">
            <div className="vc-table-card__body">
              <div className="vc-table-card__name">
                {table.displayLabel ||
                  (table.tableName &&
                  table.tableName.trim() !== String(table.tableNumber)
                    ? table.tableName.trim()
                    : `${table.spaceNoun || (table.spaceType === 'ROOM' ? 'Room' : 'Table')} ${table.tableNumber}`)}
              </div>
              <p className="vc-table-card__meta">{formatTableLabel(table)}</p>
            </div>
            <button
              type="button"
              className={`vc-table-card__btn${isConfirmed ? ' vc-table-card__btn--done' : ''}`}
              disabled={isRequesting || isConfirmed}
              onClick={() => handleSelectTable(table)}
            >
              {isRequesting ? 'Requesting…' : isConfirmed ? 'Requested' : 'Select'}
            </button>
          </article>
        );
      })}
    </div>
  );
};

export default CaptainTableCards;
